import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { registerNow, fetchTldRouting } from "@/lib/api";

/** Instant register — no availability check, no schedule. One call straight
 *  at the registrar (.nl is always routed to TransIP). */
export default function InstantRegister({ onDone }) {
  const [domain, setDomain] = useState("");
  const [listOnSuccess, setListOnSuccess] = useState(false);
  const [price, setPrice] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [routing, setRouting] = useState({});

  useEffect(() => {
    fetchTldRouting().then((r) => setRouting(r.routing || {})).catch(() => {});
  }, []);

  const tld = domain.trim().toLowerCase().split(".").pop() || "";
  const locked = routing[tld]?.[0] || null;

  const submit = async (e) => {
    e.preventDefault();
    const d = domain.trim().toLowerCase();
    if (!d.includes(".")) {
      toast.error("Enter a full domain, e.g. example.nl");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const r = await registerNow({
        domain: d,
        registrar: locked || "transip",
        list_on_success: listOnSuccess,
        lander_price: price ? Number(price) : null,
      });
      setResult(r);
      if (r.ok) {
        toast.success(`${r.domain} registered via ${r.registrar}`);
        setDomain("");
        setPrice("");
      } else {
        toast.error(r.message || "Registration failed");
      }
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="instant-register-form"
      className="border border-[#eaecf0] bg-white p-5 space-y-4"
    >
      <div>
        <div className="overline">Instant</div>
        <h2 className="text-lg font-medium tracking-tight">Register now</h2>
        <p className="text-xs font-mono text-[#525252] mt-1">
          Straight to {locked ? locked.toUpperCase() : "TransIP"} — no availability check, no schedule.
        </p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="instant-domain" className="overline">Domain</Label>
        <Input
          id="instant-domain"
          data-testid="instant-domain-input"
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          placeholder="example.nl"
          className="rounded-sm font-mono text-sm"
          autoComplete="off"
        />
      </div>

      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-sm">List for sale after</div>
          <p className="text-xs font-mono text-[#525252]">Publish a lander straight away.</p>
        </div>
        <Switch
          checked={listOnSuccess}
          onCheckedChange={setListOnSuccess}
          data-testid="instant-list-switch"
        />
      </div>

      {listOnSuccess && (
        <div className="space-y-1.5">
          <Label htmlFor="instant-price" className="overline">Asking price</Label>
          <Input
            id="instant-price"
            data-testid="instant-price-input"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            placeholder="2500"
            inputMode="numeric"
            className="rounded-sm font-mono text-sm"
          />
        </div>
      )}

      <Button
        type="submit"
        disabled={busy || !domain.trim()}
        data-testid="instant-register-btn"
        className="w-full rounded-sm bg-[#03142f] hover:bg-[#0b274d] text-white"
      >
        <Zap className="w-3.5 h-3.5 mr-2" strokeWidth={1.8} />
        {busy ? "Registering…" : "Register now"}
      </Button>

      {result && (
        <p
          data-testid="instant-register-result"
          className={`text-xs font-mono ${result.ok ? "text-[#047857]" : "text-[#BE123C]"}`}
        >
          {result.domain} · {result.message}
        </p>
      )}
    </form>
  );
}
